<template>
    <div v-if="json" class="block-timeline">
        <div class="timeline-master" ref="timelineMaster">
            <div class=" parallax-hook" ref="bgParallaxWrapper">
                <img
                    alt="Timeline background"
                    ref="bgParallax"
                    class="bgparallalx"
                    :src="window.config.json.artifacts.timelineBg"
                    :style="{ transform: 'translateX(-' + bgTranslate + 'px)' }"
                />
            </div>

            <div class="segments">
                <div class="segments-center" ref="segmentsCenter">
                    <div class="segments2" ref="segmentsContainer">
                        <div class="segment start" ref="segmentElementStart">
                            <div class="main-panel">
                                <div class="top-images"></div>
                                <div class="segment-content">
                                    <div class="segment-content2">
                                        <div class="label" v-html="formatMainLabel(json.timelineTitle)"></div>
                                        <div class="caption" v-html="json.timelineBlurb"></div>
                                    </div>
                                </div>
                                <div class="bottom-images"></div>
                            </div>
                        </div>

                        <div
                            v-for="(item, sIndex) in json.timelineSegments"
                            :key="`segment${sIndex}`"
                            class="segment"
                            :class="`segment-${sIndex}`"
                            ref="segmentElements"
                        >
                            <div class="main-panel">
                                <div class="top-images" :class="getSegmentPatternClass(item.timelineTopGraphics, sIndex)">
                                    <div
                                        v-for="(topg, tgIndex) in item.timelineTopGraphics"
                                        :key="`topg${tgIndex}`"
                                        class="timelimeimg imgover"
                                        :class="`topg-${tgIndex}`"
                                        :style="[topg.bgStyle, `aspect-ratio:${topg.aspectRatio}`]"
                                        @click="openSlideshow(getGlobalIndex(sIndex, 'top', tgIndex))"
                                    >
                                        <div class="plus-circle"><span>+</span></div>
                                    </div>
                                </div>
                                <div class="segment-content">
                                    <div class="vertical-line"><span></span></div>
                                    <div class="info">
                                        <div class="title-line">
                                            <div class="years">{{ item.yearRange }}</div>
                                            <div class="devices">({{ item.yearRangeDevices }})</div>
                                            <div class="dots">
                                                <span class="dot"></span>
                                                <span class="dot-fill" :style="`background-image:url('${window.config.json.artifacts.dots}')`"></span>
                                                <span class="dot"></span>
                                            </div>
                                        </div>
                                        <div class="logos">
                                            <img
                                                v-for="(logo, lIndex) in item.timelineLogos"
                                                :key="`logo${lIndex}`"
                                                class="logo"
                                                :class="(logo.aspectRatio < 1) ? 'portrait' : 'landscape'"
                                                :src="logo.url"
                                                :alt="logo.title"
                                            />
                                        </div>
                                        <div class="caption" v-html="item.caption"></div>
                                    </div>
                                </div>
                                <div class="bottom-images" :class="getSegmentPatternClass(item.timelineBottomGraphics, sIndex)">
                                    <div
                                        v-for="(bottomg, ggIndex) in item.timelineBottomGraphics"
                                        :key="`bottomg${ggIndex}`"
                                        class="timelimeimg imgover"
                                        :class="`bottomg-${ggIndex}`"
                                        :style="[bottomg.bgStyle, `aspect-ratio:${bottomg.aspectRatio}`]"
                                        @click="openSlideshow(getGlobalIndex(sIndex, 'bottom', ggIndex))"
                                    >
                                        <div class="plus-circle"><span>+</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="left-corner">
            <TtmotifIcon class="tt-motif-svg" aria-label="Insomniac" />
        </div>

        <div class="right-corner">
            <div class="dots">
                <template v-for="n in totalSlides" :key="n">
          <span
              class="dot"
              :class="{ active: currentSlide === (n - 1) }"
              @click="goToSlide(n - 1)"
          ></span>
                    <span v-if="n < totalSlides" class="dot-fill" :style="`background-image:url('${window.config.json.artifacts.dots}')`"></span>
                </template>
                <div class="square-outer" :style="squareTranslate">
                    <div class="square"></div>
                </div>
            </div>
            <div class="btns">
                <div class="swiper-button swiper-button-prev" @click="prevSlide"></div>
                <div class="swiper-button swiper-button-next" @click="nextSlide"></div>
            </div>
        </div>
    </div>
</template>

<script>
import gsap from '@/plugins/gsap';
import ScrollTrigger from '@/plugins/gsap/ScrollTrigger';
import gh from '@/helpers/gh'
import TtmotifIcon from "@/components/svgs/Ttmotif.vue";

gsap.registerPlugin(ScrollTrigger)

export default {
    name: 'TimelineBlock',
    components: {TtmotifIcon},
    props: {
        json: {type: Object, required: true},
        type: {type: String, required: true}
    },
    data() {
        return {
            window:window,
            gh: gh,
            currentSlide: 0,
            slideTranslate: 0,
            bgTranslate: 0,
            combinedArray: [],
            isAnimating: false,
            touchStartX: 0,
            touchEndX: 0,
            isMouseDown: false,
            mouseStartX: 0,
            mouseEndX: 0,
            revealDone: false,
            isMobile: window.innerWidth < 850
        };
    },
    computed: {
        totalSlides() {
            if (this.json && this.json.timelineSegments) {
                return this.isMobile
                    ? 1 + this.json.timelineSegments.length
                    : 1 + (this.json.timelineSegments.length > 0 ? this.json.timelineSegments.length - 1 : 0);
            }
            return 1;
        },
        squareTranslate() {
            let ret = 34 * this.currentSlide;
            return `transform: translateX(${ret}px);`;
        }
    },
    mounted() {
        this.$nextTick(() => {
            if (this.$refs.bgParallax) {
                this.$refs.bgParallax.style.transition = "transform 0.5s ease";
            }

            // Verison 1 come in from off screen
            // const offscreenX = window.innerWidth;
            // if (!this.revealDone) {
            //     if (this.$refs.segmentElementStart) {
            //         this.$refs.segmentElementStart.style.transition = "transform 0.5s ease";
            //         this.$refs.segmentElementStart.style.transform = `translateX(${offscreenX}px)`;
            //     }
            //     const segments = this.$refs.segmentElements;
            //     if (segments && segments.length) {
            //         segments.forEach(seg => {
            //             seg.style.transition = "transform 0.5s ease";
            //             seg.style.transform = `translateX(${offscreenX}px)`;
            //         });
            //     }
            // }
            if (!this.revealDone) {
                if (this.$refs.segmentElementStart) {
                    this.$refs.segmentElementStart.style.transition = "transform 0.5s ease, opacity 0.5s ease";
                    this.$refs.segmentElementStart.style.transform = "translateX(200px)";
                    this.$refs.segmentElementStart.style.opacity = "0";
                }
                const segments = this.$refs.segmentElements;
                if (segments && segments.length) {
                    segments.forEach(seg => {
                        seg.style.transition = "transform 0.5s ease, opacity 0.5s ease";
                        seg.style.transform = "translateX(100px)";
                        seg.style.opacity = "0";
                    });
                }
            }


            window.addEventListener("resize", this.handleResize);
            if (this.$refs.timelineMaster) {
                this.$refs.timelineMaster.addEventListener("touchstart", this.handleTouchStart);
                this.$refs.timelineMaster.addEventListener("touchend", this.handleTouchEnd);
                this.$refs.timelineMaster.addEventListener("mousedown", this.handleMouseDown);
                this.$refs.timelineMaster.addEventListener("mousemove", this.handleMouseMove);
                this.$refs.timelineMaster.addEventListener("mouseup", this.handleMouseUp);
                this.$refs.timelineMaster.addEventListener("mouseleave", this.handleMouseUp);
            }

            if (this.$refs.segmentsContainer) {
                this.$refs.segmentsContainer.addEventListener("transitionend", this.onTransitionEnd);
            }

            this.combinedArray = [];
            if (this.json && this.json.timelineSegments) {
                this.json.timelineSegments.forEach(segment => {
                    if (segment.timelineTopGraphics) {
                        this.combinedArray.push(...segment.timelineTopGraphics);
                    }
                    if (segment.timelineBottomGraphics) {
                        this.combinedArray.push(...segment.timelineBottomGraphics);
                    }
                });
            }

            const observer = new IntersectionObserver(
                (entries, obs) => {
                    entries.forEach(entry => {
                        if (entry.isIntersecting && entry.intersectionRatio >= 0.35 && !this.revealDone) {
                            this.firstReveal();
                            obs.unobserve(entry.target);
                        }
                    });
                },
                {threshold: [0.35]}
            );

            if (this.$refs.timelineMaster) {
                observer.observe(this.$refs.timelineMaster);
            }
        });
    },
    beforeUnmount() {
        window.removeEventListener("resize", this.handleResize);
        if (this.$refs.segmentsContainer) {
            this.$refs.segmentsContainer.removeEventListener("transitionend", this.onTransitionEnd);
        }
        if (this.$refs.timelineMaster) {
            this.$refs.timelineMaster.removeEventListener("touchstart", this.handleTouchStart);
            this.$refs.timelineMaster.removeEventListener("touchend", this.handleTouchEnd);
            this.$refs.timelineMaster.removeEventListener("mousedown", this.handleMouseDown);
            this.$refs.timelineMaster.removeEventListener("mousemove", this.handleMouseMove);
            this.$refs.timelineMaster.removeEventListener("mouseup", this.handleMouseUp);
            this.$refs.timelineMaster.removeEventListener("mouseleave", this.handleMouseUp);
        }
    },
    methods: {
        handleResize() {
            this.isMobile = window.innerWidth < 850;
            this.updateSlidePosition();
        },
        handleTouchStart(e) {
            this.touchStartX = e.changedTouches[0].screenX;
        },
        handleTouchEnd(e) {
            this.touchEndX = e.changedTouches[0].screenX;
            this.handleSwipeGesture(this.touchStartX, this.touchEndX);
        },
        handleMouseDown(e) {
            this.isMouseDown = true;
            this.mouseStartX = e.screenX;
            e.preventDefault();
        },
        handleMouseMove(e) {
            if (this.isMouseDown) {
                e.preventDefault();
            }
        },
        handleMouseUp(e) {
            if (!this.isMouseDown) return;
            this.isMouseDown = false;
            this.mouseEndX = e.screenX;
            this.handleSwipeGesture(this.mouseStartX, this.mouseEndX);
        },
        handleSwipeGesture(startX, endX) {
            const threshold = 50;
            const diff = startX - endX;
            if (Math.abs(diff) > threshold) {
                if (diff > 0) {
                    this.nextSlide();
                } else {
                    this.prevSlide();
                }
            }
        },
        getGlobalIndex(segmentIndex, area, index) {
            let globalIndex = 0;
            for (let i = 0; i < segmentIndex; i++) {
                const segment = this.json.timelineSegments[i];
                globalIndex += segment.timelineTopGraphics?.length || 0;
                globalIndex += segment.timelineBottomGraphics?.length || 0;
            }
            if (area === "top") {
                globalIndex += index;
            } else if (area === "bottom") {
                const currentTopCount = this.json.timelineSegments[segmentIndex].timelineTopGraphics?.length || 0;
                globalIndex += currentTopCount + index;
            }
            return globalIndex;
        },
        formatMainLabel(str) {
            str = str.replace(/\n/g, "XXX").replace(/\+/g, "<sup>+</sup>");
            return str.split("XXX").map(part => `<div>${part}</div>`).join("");
        },
        getSegmentPatternClass(imagesArray, segmentIndex) {
            const totalImages = imagesArray.length;
            if (totalImages === 1) {
                return `p1-${((segmentIndex - 1) % 2) + 1}`;
            } else if (totalImages === 2) {
                return `p2-${(segmentIndex % 3) + 1}`;
            }
            return "";
        },
        updateSlidePosition() {
            const segmentsCenter = this.$refs.segmentsCenter;
            const startEl = this.$refs.segmentElementStart;
            const timelineEls = Array.from(this.$refs.segmentElements);
            if (!segmentsCenter || !startEl || !timelineEls) return;
            const containerWidth = segmentsCenter.offsetWidth;
            let translation = 0;
            let effectiveSlides = [];
            effectiveSlides.push(startEl);
            if (this.isMobile) {
                effectiveSlides.push(...timelineEls);
            } else {
                if (timelineEls.length > 1) {
                    effectiveSlides.push(...timelineEls.slice(1));
                }
            }
            for (let i = 0; i < this.currentSlide; i++) {
                const slide = effectiveSlides[i];
                const style = window.getComputedStyle(slide);
                const mr = parseFloat(style.marginRight) || 0;
                translation += slide.offsetWidth + mr;
            }
            this.slideTranslate = translation;
            let totalWidth = 0;
            const startStyle = window.getComputedStyle(startEl);
            const startMargin = parseFloat(startStyle.marginRight) || 0;
            totalWidth += startEl.offsetWidth + startMargin;
            timelineEls.forEach(el => {
                const style = window.getComputedStyle(el);
                const mr = parseFloat(style.marginRight) || 0;
                totalWidth += el.offsetWidth + mr;
            });
            const maxTranslate = totalWidth - containerWidth;
            const pct = maxTranslate > 0 ? Math.min(translation / maxTranslate, 1) : 0;
            const bgWidth = this.$refs.bgParallax.offsetWidth;
            const windowWidth = window.innerWidth;
            const maxBgTranslate = bgWidth > windowWidth ? bgWidth - windowWidth : 0;
            this.bgTranslate = pct * maxBgTranslate;
        },
        animatePanels(targetTranslate, direction = "next") {
            const staggerDelay = 50;
            setTimeout(() => {
                if (this.$refs.bgParallax) {
                    this.$refs.bgParallax.style.transition = "transform 1.05s ease";
                    this.$refs.bgParallax.style.transform = `translateX(-${this.bgTranslate}px)`;
                }
            }, staggerDelay);
            let effectiveSlides = [];
            effectiveSlides.push(this.$refs.segmentElementStart);
            const timelineEls = Array.from(this.$refs.segmentElements);
            effectiveSlides.push(...timelineEls);
            if (direction === "prev") {
                effectiveSlides = effectiveSlides.reverse();
            }
            effectiveSlides.forEach((slide, index) => {
                slide.style.transition = "transform 0.5s ease";
                setTimeout(() => {
                    slide.style.transform = `translateX(-${targetTranslate}px)`;
                }, staggerDelay * (index + 1));
            });
        },
        nextSlide() {
            if (this.isAnimating) return;
            if (this.currentSlide < this.totalSlides - 1) {
                this.isAnimating = true;
                this.currentSlide++;
                this.updateSlidePosition();
                this.animatePanels(this.slideTranslate, "next");
                setTimeout(() => {
                    this.isAnimating = false;
                }, 500 + 50 * (this.$refs.segmentElements.length + 1));
            }
        },
        prevSlide() {
            if (this.isAnimating) return;
            if (this.currentSlide > 0) {
                this.isAnimating = true;
                this.currentSlide--;
                this.updateSlidePosition();
                this.animatePanels(this.slideTranslate, "prev");
                setTimeout(() => {
                    this.isAnimating = false;
                }, 500 + 50 * (this.$refs.segmentElements.length + 1));
            }
        },
        goToSlide(slideIndex) {
            if (this.isAnimating || this.currentSlide === slideIndex) return;
            this.isAnimating = true;
            const direction = slideIndex > this.currentSlide ? "next" : "prev";
            this.currentSlide = slideIndex;
            this.updateSlidePosition();
            this.animatePanels(this.slideTranslate, direction);
            setTimeout(() => {
                this.isAnimating = false;
            }, 500 + 50 * (this.$refs.segmentElements.length + 1));
        },
        onTransitionEnd() {
        },
        firstReveal() {
            this.revealDone = true;
            const staggerDelay = 150;
            if (this.$refs.segmentElementStart) {
                setTimeout(() => {
                    this.$refs.segmentElementStart.style.transform = "translateX(0)";
                    this.$refs.segmentElementStart.style.opacity = "1";
                }, staggerDelay);
            }
            const segments = this.$refs.segmentElements;
            if (segments && segments.length) {
                segments.forEach((seg, index) => {
                    setTimeout(() => {
                        seg.style.transform = "translateX(0)";
                        seg.style.opacity = "1";
                    }, staggerDelay * (index + 1));
                });
            }
        },
        openSlideshow(index) {
            this.$store.commit("openPopup", {
                gallery: true,
                startingIndex: index,
                items: this.combinedArray
            });
        }
    }
};
</script>
